import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Star, MessageSquare } from "lucide-react";
import { useOrganizerReviews } from "@/hooks/useOrganizerReviews";
import { useOrganizerReview } from "@/hooks/useOrganizerReview";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface OrganizerReviewsSectionProps {
  organizerId: string;
}

const StarRating = ({ rating }: { rating: number }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`h-4 w-4 ${
          star <= rating
            ? 'fill-yellow-400 text-yellow-400'
            : star - 0.5 <= rating
            ? 'fill-yellow-400/50 text-yellow-400'
            : 'text-muted-foreground/30'
        }`}
      />
    ))}
  </div>
);

const InteractiveStars = ({ rating, onSelect }: { rating: number; onSelect: (r: number) => void }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        onClick={() => onSelect(star)}
        className="focus:outline-none"
      >
        <Star
          className={`h-7 w-7 transition-colors ${
            star <= rating
              ? 'fill-yellow-400 text-yellow-400'
              : 'text-muted-foreground/30 hover:text-yellow-400/50'
          }`}
        />
      </button>
    ))}
  </div>
);

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const OrganizerReviewsSection = ({ organizerId }: OrganizerReviewsSectionProps) => {
  const { reviews, averageRating, loading, totalReviews } = useOrganizerReviews(organizerId);
  const { user } = useAuth();
  const { hasReviewed, existingReview, loading: reviewLoading, submitting, submitReview } = useOrganizerReview(organizerId);

  const [selectedRating, setSelectedRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const handleSubmit = async () => {
    if (selectedRating === 0) return;
    const success = await submitReview(selectedRating, comment);
    if (success) {
      setSelectedRating(0);
      setComment("");
      setIsEditing(false);
    }
  };

  const startEditing = () => {
    if (existingReview) {
      setSelectedRating(existingReview.rating);
      setComment(existingReview.comment || "");
    }
    setIsEditing(true);
  };

  return (
    <Card className="bg-white">
      <CardHeader>
        <CardTitle className="text-xl font-bold text-foreground flex items-center">
          <Star className="h-5 w-5 mr-2" />
          Reseñas y Calificaciones
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Average rating summary */}
            {totalReviews > 0 && (
              <div className="flex items-center gap-4 p-4 bg-[#31633f]/15 rounded-xl border border-[#31633f]/30">
                <div className="text-3xl font-bold text-foreground">{averageRating}</div>
                <div className="flex flex-col gap-1">
                  <StarRating rating={Math.round(averageRating)} />
                  <span className="text-sm text-muted-foreground">
                    {totalReviews} {totalReviews === 1 ? 'reseña' : 'reseñas'}
                  </span>
                </div>
              </div>
            )}

            {/* Reviews list */}
            {totalReviews > 0 ? (
              <div className="space-y-3">
                {reviews.map((review) => (
                  <div key={review.id} className="p-3 rounded-lg border border-border bg-card">
                    <div className="flex items-center gap-3 mb-1">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={review.userAvatar || undefined} />
                        <AvatarFallback className="bg-primary/10 text-primary text-xs">
                          {review.userName.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{review.userName}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <StarRating rating={review.rating} />
                        <span className="text-xs text-muted-foreground">{formatDate(review.created_at)}</span>
                      </div>
                    </div>
                    {review.comment && (
                      <p className="text-sm text-muted-foreground leading-relaxed pl-11">
                        "{review.comment}"
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <MessageSquare className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">Aún no hay reseñas</p>
              </div>
            )}

            {/* Review form */}
            {!reviewLoading && (
              <div className="pt-4 border-t border-border">
                {!user ? (
                  <p className="text-sm text-muted-foreground text-center">
                    Inicia sesión para calificar a este organizador
                  </p>
                ) : hasReviewed && !isEditing ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Tu calificación:</span>
                      <StarRating rating={existingReview?.rating || 0} />
                    </div>
                    <Button variant="ghost" size="sm" onClick={startEditing}>
                      Editar
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-foreground">
                      {isEditing ? 'Editar tu calificación' : 'Calificar organizador'}
                    </p>
                    <InteractiveStars rating={selectedRating} onSelect={setSelectedRating} />
                    <Textarea
                      placeholder="Comentario opcional..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      className="min-h-[60px]"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleSubmit}
                        disabled={selectedRating === 0 || submitting}
                      >
                        {submitting ? 'Enviando...' : 'Enviar'}
                      </Button>
                      {isEditing && (
                        <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)}>
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OrganizerReviewsSection;
